import { Component, Injector, OnInit } from '@angular/core';
import { Athlete } from "../../models/athlete";
import { AthleteService } from "../../services/athlete/athlete.service";
import { ActivatedRoute, Router } from "@angular/router";
import { BaseComponent } from "../base/base.component";
import { CategoryService } from "../../services/category/category.service";
import { Category } from "../../models/category";
import { User } from "../../models/user";
import { UserService } from "../../services/user/user.service";
import { Parent } from "../../models/parent";
import { Season } from "../../models/season";
import Swal from "sweetalert2";
import * as moment from "moment";
import { Permissions } from "../../resources/permissions";
import { athleteSeasonStatus, AthleteStatusInfo } from "../../resources/athlete-status";
import { PaymentService } from "../../services/payment/payment.service";
import { installmentStatusLabel } from "../recurring-payment-details/recurring-payment-details.component";

interface ProfileSection {
    title: string;
    description: string;
    icon: string;
    route: any[];
    /** `info` = sezione consultiva, non conta nel completamento. */
    state: 'done' | 'todo' | 'info';
    badge?: string;
}

@Component({
    selector: 'app-edit-profile',
    templateUrl: './edit-profile.component.html'
})
export class EditProfileComponent extends BaseComponent implements OnInit {

    athlete: Athlete = new Athlete();
    user: User = new User();
    currentCategoryId: string | undefined;
    currentSeason: Season | undefined;
    category: Category | undefined;
    athleteStatus: string | undefined;
    athleteHasParents: boolean = false;
    parents: Parent[] = [];
    reviewing: boolean = false;
    missingDocumentsAmount: number = 0;
    isUserCompanyManager: boolean = localStorage.getItem('wisper_userGroup') === 'companyManager';
    userHasCompletedMembership: boolean = false;
    editMemberShip: boolean = false;
    textArea: string | undefined = '';
    paymentRequest?: any = ''
    installments: {number: number, amount: number, status: string, scheduledDate: string, paidAt: string | null}[] = [];
    readonly installmentStatusLabel = installmentStatusLabel;
    userSubType?: string = ''
    userPermission?: boolean = false
    isUser: boolean = localStorage.getItem('wisper_userGroup') === 'user';
    loading: boolean = true;

    constructor(
        public athleteService: AthleteService,
        public userService: UserService,
        public categoryService: CategoryService,
        private paymentService: PaymentService,
        private route: ActivatedRoute,
        public serviceInjector: Injector, public router: Router
    ) {
        super(serviceInjector);
    }

    async ngOnInit() {
        let athleteId = '';
        if (this.route.snapshot.params['athleteId'] && this.isUUID(this.route.snapshot.params['athleteId'])) {
            athleteId = this.route.snapshot.params['athleteId'];
        }
        this.editMemberShip = this.hasPermission(Permissions.createSeason) ?? false;
        this.userPermission = !!(this.hasPermission(Permissions.globalVisibility) || this.hasPermission(Permissions.companyVisibility));

        try {
            this.athlete = await this.athleteService.getAthleteById(athleteId);
            this.currentSeason = this.athlete.seasons.find((season: Season) => season.isCurrent);
            this.currentCategoryId = this.currentSeason?.AthleteSeason.categoryId;
            this.textArea = this.athlete.note;
            this.athleteStatus = this.athlete.status;
            this.userHasCompletedMembership = this.athlete.memberships.length > 0;

            const companyId = localStorage.getItem('wisper_companyId') ?? '';
            const [category, user, parents, missingDocuments, paymentRequest] = await Promise.all([
                this.currentCategoryId ? this.categoryService.getCategoryById(this.currentCategoryId) : Promise.resolve(undefined),
                this.userService.getUserById(this.athlete.userId),
                this.athleteService.getAthleteParents(athleteId),
                this.athleteService.checkAthleteMandatoryDocuments(athleteId, companyId),
                this.athleteService.paymentRequestByAthleteId(athleteId),
            ]);

            this.category = category;
            this.user = user;
            this.userSubType = user?.subtype;
            this.parents = parents ?? [];
            this.athleteHasParents = this.parents.length > 0;
            this.missingDocumentsAmount = missingDocuments ? missingDocuments.length : 0;
            this.paymentRequest = paymentRequest;
            if (this.userPermission && paymentRequest?.data?.status === 'partialPayment') {
                const details = await this.paymentService.getRecurringPaymentDetails(paymentRequest.data.id).catch(() => null);
                this.installments = details?.data?.installments ?? [];
            }
        } catch (e) {
            console.error('error loading athlete profile: ', e);
        } finally {
            this.loading = false;
        }
    }

    get status(): AthleteStatusInfo {
        // The season is 'paid' as soon as an installment plan starts: show the plan is still running.
        const partial = this.athlete.seasonStatus === 'paid' && this.paymentRequest?.data?.status === 'partialPayment';
        return athleteSeasonStatus(partial ? 'partialPayment' : this.athlete.seasonStatus);
    }

    get paidInstallments(): number {
        return this.installments.filter(installment => installment.status === 'completed').length;
    }

    get canPay(): boolean {
        return !this.athlete.seasonStatus || this.athlete.seasonStatus === 'pendingPayment';
    }

    /** Metodo del pagamento offline ancora da completare (bonifico o segreteria), se presente. */
    get pendingOfflinePaymentMethod(): 'transfer' | 'localPayment' | null {
        const request = this.paymentRequest?.data;
        if (request?.status !== 'pendingPayment') return null;
        return request.paymentMethod === 'transfer' || request.paymentMethod === 'localPayment' ? request.paymentMethod : null;
    }

    get receiptUrl(): string | null {
        return this.paymentRequest?.data?.status === 'paid' ? (this.paymentRequest.data.receiptUrl || null) : null;
    }

    /** Sezioni del profilo con il relativo stato di completamento. */
    get sections(): ProfileSection[] {
        const sections: ProfileSection[] = [
            {
                title: 'Informazioni atleta',
                description: 'Anagrafica, residenza, contatti e foto',
                icon: 'bi-person-vcard',
                route: ['/users/edit-athlete', this.athlete.id],
                state: this.checkAthleteInformation() ? 'done' : 'todo',
                badge: this.checkAthleteInformation() ? 'Completato' : 'Da completare',
            },
            {
                title: 'Documenti',
                description: 'Carica i documenti richiesti dalla società',
                icon: 'bi-file-earmark-text',
                route: ['/users/documents', this.athlete.id],
                state: this.missingDocumentsAmount === 0 ? 'done' : 'todo',
                badge: this.missingDocumentsAmount === 0 ? 'Completato'
                    : (this.missingDocumentsAmount === 1 ? '1 mancante' : `${this.missingDocumentsAmount} mancanti`),
            },
            this.parentsSection(),
            {
                title: 'Dettaglio iscrizioni',
                description: 'Stagioni, categorie e ricevute',
                icon: 'bi-receipt',
                route: ['/users/subscriptions', this.athlete.id],
                state: 'info',
            },
        ];
        // Athletes only see their membership once it exists, read-only
        if (this.editMemberShip || (this.isUser && this.userHasCompletedMembership)) {
            sections.push({
                title: 'Tesseramento',
                description: 'Gestione delle tessere dell\'atleta',
                icon: 'bi-person-badge',
                route: ['/users/membership', this.athlete.id],
                state: this.userHasCompletedMembership ? 'done' : 'todo',
                badge: this.userHasCompletedMembership ? 'Tesserato' : 'Non tesserato',
            });
        }
        return sections;
    }

    trackSection(_: number, section: ProfileSection): string {
        return section.title;
    }

    get trackedSections(): ProfileSection[] {
        return this.sections.filter(section => section.state !== 'info');
    }

    get completedSectionsCount(): number {
        return this.trackedSections.filter(section => section.state === 'done').length;
    }

    goBack() {
        if (this.isUser) {
            this.router.navigate(['/users/dashboard']);
        } else {
            this.previousPage();
        }
    }

    /** La società può approvare o rifiutare un atleta in attesa, indipendentemente da come è arrivata sulla pagina. */
    get canReviewAthlete(): boolean {
        return this.isUserCompanyManager && this.athlete.status === 'pendingCompanyApprovation';
    }

    async rejectAthlete(athleteId: string) {
        const confirm = await Swal.fire({
            icon: 'warning',
            title: 'Disattivare il profilo dell\'atleta?',
            text: 'L\'atleta non potrà procedere con l\'iscrizione finché il profilo non viene riattivato.',
            showCancelButton: true,
            confirmButtonText: 'Sì, disattiva',
            cancelButtonText: 'Annulla'
        });
        if (!confirm.isConfirmed) return;

        await this.reviewAthlete(() => this.athleteService.rejectAthlete(athleteId), 'Profilo atleta disattivato');
    }

    async regenerateReceipt() {

        const result = await Swal.fire({
            icon: 'warning',
            title: "Sei sicuro di voler rigenerare la ricevuta?",
            text: "Verifica che tutti i dati dell'aleta e del genitore/i siano completati prima di procedere.",
            showCancelButton: true,
            confirmButtonText: "Si, rigenera la ricevuta",
            cancelButtonText: "No, annulla l'operazione"
        });
        if (result.isConfirmed) {
            this.showSpinner()

            const response = await this.athleteService.regenerateReceipt(this.paymentRequest.data.id);

            if (response) {
                this.hideSpinner()
                Swal.fire({
                    icon: 'success',
                    title: 'Ricevuta ricreata con successo!'
                }).then(() => {
                    window.location.reload()
                })
            } else {
                this.hideSpinner()
                Swal.fire({
                    icon: 'error',
                    title: "Errore durante l'operazione"
                })
            }
        }
    }

    async approveAthlete(athleteId: string) {
        await this.reviewAthlete(() => this.athleteService.approveAthlete(athleteId), 'Profilo atleta attivato');
    }

    private async reviewAthlete(action: () => Promise<any>, successTitle: string) {
        this.reviewing = true;
        try {
            const response = await action();
            if (response?.success) {
                this.athlete = await this.athleteService.getAthleteById(this.athlete.id!);
                this.athleteStatus = this.athlete.status;
                Swal.fire({icon: 'success', title: successTitle});
            } else {
                Swal.fire({icon: 'error', title: "Errore durante l'operazione"});
            }
        } finally {
            this.reviewing = false;
        }
    }

    checkAthleteInformation() {
        // return this.athlete.

        return (
            this.athlete.firstName &&
            this.athlete.lastName &&
            this.athlete.bornDate &&
            this.athlete.bornCity &&
            this.athlete.address &&
            this.athlete.city &&
            this.athlete.province &&
            this.athlete.phone
        )

    }

    /** Un genitore è richiesto per gli atleti minorenni (o di età sconosciuta) e per gli account genitore. */
    get parentRequired(): boolean {
        if (this.userSubType == 'userParent') {
            return true;
        }
        const bornDate = moment(this.athlete.bornDate);
        return !bornDate.isValid() || moment().diff(bornDate, 'years') < 18;
    }

    private parentsSection(): ProfileSection {
        const base = {title: 'Gestione genitori', icon: 'bi-people', route: ['/users/edit-parents', this.athlete.id]};
        if (!this.athleteHasParents) {
            return this.parentRequired
                ? {...base, description: 'Nessun genitore registrato: aggiungine almeno uno', state: 'todo', badge: 'Da aggiungere'}
                : {...base, description: 'Facoltativo per atleti maggiorenni', state: 'info'};
        }
        const complete = this.checkParentsInformation();
        return {
            ...base,
            description: this.parents.length === 1 ? '1 genitore registrato' : `${this.parents.length} genitori registrati`,
            state: complete ? 'done' : 'todo',
            badge: complete ? 'Completato' : 'Da completare',
        };
    }

    checkParentsInformation() {
        if (!this.athleteHasParents) {
            return !this.parentRequired;
        }

        return this.parents.every((parent: Parent) => {
            return (parent.firstName &&
                parent.lastName &&
                parent.address &&
                parent.city &&
                parent.bornCity);
        });
    }

    athleteCompleted(): boolean {
        return !!(
            this.athlete.firstName?.trim() &&
            this.athlete.lastName?.trim() &&
            this.athlete.bornCity?.trim() &&
            this.athlete.bornDate?.trim() &&
            this.athlete.fiscalCode?.trim() &&
            this.athlete.address?.trim() &&
            this.athlete.city?.trim() &&
            this.athlete.province?.trim() &&
            this.athlete.phone?.trim() &&
            this.athlete.email?.trim()
        );
    }

    async saveText() {
        this.athlete.note = this.textArea
        let response: any = await this.athleteService.updateAthlete(this.athlete);
        if (response) {
            Swal.fire({
                icon: 'success',
                title: 'Note salvate con successo'
            })
        } else {
            Swal.fire({
                icon: 'error',
                title: "Errore nel salvataggio delle note"
            })
        }
    }

    async deleteAthlete() {
        Swal.fire({
            icon: "question",
            title: "Sei sicuro di voler eliminare l'atleta?",
            showCancelButton: true,
            confirmButtonText: 'Si, procedi',
            cancelButtonText: "Annulla l'operazione"
        })
            .then(async (result) => {
                if (result.isConfirmed && this.athlete.id) {
                    this.showSpinner();
                    let athleteResponse = await this.athleteService.deleteAthlete(this.athlete.id);
                    this.hideSpinner();

                    if (athleteResponse.success) {
                        Swal.fire({
                            icon: 'success',
                            title: 'Atleta eliminato con successo'
                        }).then(() => {
                            this.router.navigate(['/users/dashboard'], {relativeTo: this.route});
                        });
                    } else {
                        Swal.fire({
                            icon: 'error',
                            title: "Errore nell'eliminazione dell'atleta"
                        })
                    }
                }
            });

    }
}
